/**
 * Recognizing chords in pasted text.
 *
 * Chord recognition is the shared chord grammar (parseChordStringStrict); this
 * module only adds the chart-level notation around it: alias spellings the
 * grammar doesn't read, markers like "N.C." and "x2", bar lines, and the
 * heuristics that tell a chord line from a lyric line.
 */
import { parseChordStringStrict } from '../chordParser.js';
import { displayWidth } from './columns.js';
/**
 * Capitalized words that are also chord names. A line whose only chords are
 * these ("Am I the one", "A man walks in") is not taken as a chord line.
 */
const WORD_CHORDS = new Set(['A', 'Am', 'E', 'Em']);
/** Chord-chart notation that isn't a chord but belongs on a chord line. Kept as custom text. */
const MARKERS = [
    /^N\.?C\.?$/i, // no chord
    /^\(?[x×]\d{1,2}\)?$/i, // x2, (x2), ×4
    /^\(?\d{1,2}[x×]\)?$/i, // 2x, (2x)
    /^%$/, // repeat the previous bar
    /^\(?tacet\)?$/i,
];
/** Bar lines, beat slashes, dots, ellipses and dashes: rhythm notation between chords, dropped. */
const SEPARATOR = /^[|:./\\…-]+$/;
const TOKEN = /\([^()]*\)|\[[^\]]*\]|\S+/g;
const FOOTNOTE_OR_COMMA = /^\*+|[*,;]+$/g;
const WIDE_GAP = /\S\s{2,}\S/;
/**
 * Rewrites spellings common in charts that the chord grammar doesn't read:
 * mixed-case quality words ("Maj7", "SUS4"), "sus" without a number (sus4),
 * "(add9)", "Δ" for maj7 and "ø" for half-diminished.
 */
const normalizeChordSpelling = (token) => {
    const match = token.match(/^([A-G][#b♯♭]?)(.*)$/);
    if (!match)
        return token;
    const suffix = match[2]
        .replace(/maj|min|sus|add|dim|aug/gi, word => word.toLowerCase())
        .replace(/[Δ△](?=\d)/g, 'maj')
        .replace(/[Δ△]/g, 'maj7')
        .replace(/[øØ]7?/g, 'm7♭5')
        .replace(/\((add\d+|sus[24]?)\)/g, '$1')
        .replace(/sus(?![24])/g, 'sus4');
    return match[1] + suffix;
};
/** Reads a chart token as a chord ("Am7", "F#m/C#", "Gsus"), or null if it isn't one. */
export const readChord = (token) => parseChordStringStrict(normalizeChordSpelling(token));
export const isMarker = (text) => MARKERS.some(pattern => pattern.test(text));
const REPEAT_COUNT = /^\(?(?:[x×]\s?(\d{1,2})|(\d{1,2})\s?[x×])\)?$/i;
/** The count of a repeat marker ("x2", "(4x)", "×3"), or null for anything else. */
export const readRepeatCount = (text) => {
    const match = text.trim().match(REPEAT_COUNT);
    return match ? Number(match[1] ?? match[2]) : null;
};
/** A chord that can't be mistaken for an ordinary word. */
export const isUnambiguousChord = (text) => !WORD_CHORDS.has(text) && readChord(text) !== null;
const classifyToken = (text) => {
    if (isMarker(text))
        return { kind: 'marker', chord: null };
    const bare = text.replace(FOOTNOTE_OR_COMMA, '');
    const chord = bare ? readChord(bare) : null;
    if (!chord)
        return { kind: 'other', chord: null };
    return { kind: WORD_CHORDS.has(bare) ? 'wordChord' : 'chord', chord };
};
const isChordKind = (token) => token.kind === 'chord' || token.kind === 'wordChord';
/**
 * Splits a line into tokens with their columns, dropping separators. A group
 * in parentheses or brackets holding only chords — "(G)", a reminder of the
 * chord still ringing — is unwrapped into those chords.
 */
export const tokenizeLine = (line) => {
    const tokens = [];
    for (const match of line.matchAll(TOKEN)) {
        const text = match[0];
        const index = match.index ?? 0;
        if (SEPARATOR.test(text))
            continue;
        if (/^[([]/.test(text) && text.length > 2) {
            const inner = tokenizeLine(text.slice(1, -1));
            if (inner.length > 0 && inner.every(isChordKind)) {
                for (const token of inner) {
                    const innerIndex = index + 1 + token.index;
                    tokens.push({ ...token, index: innerIndex, column: displayWidth(line.slice(0, innerIndex)) });
                }
                continue;
            }
        }
        tokens.push({ text, index, column: displayWidth(line.slice(0, index)), ...classifyToken(text) });
    }
    return tokens;
};
/** Two words in a row with a single space between them: a phrase, i.e. lyrics. */
const hasPhrase = (line, tokens) => tokens.some((token, i) => {
    const previous = tokens[i - 1];
    return (i > 0 && token.kind === 'other' && previous.kind === 'other' &&
        line.slice(previous.index + previous.text.length, token.index) === ' ');
});
/** A chord or chart marker that can't be an ordinary word ("G7", "N.C.", "x2"; not "A" or "Am"). */
const isUnambiguousNotation = (token) => token.kind === 'chord' || token.kind === 'marker';
/**
 * The tokens of a chord line, or null if the line reads as lyrics.
 *
 * A line is a chord line when all of its tokens are chord notation, or when
 * it has unambiguous notation, the other tokens don't form a phrase, and
 * chord notation makes up 60% of the tokens (50% when the tokens are spread
 * out with wide gaps, as chord lines are). The other tokens ("Riff",
 * "(let ring)") become custom text.
 */
export const readChordLine = (line) => {
    const tokens = tokenizeLine(line);
    if (tokens.length === 0)
        return null;
    const notation = tokens.filter(token => token.kind !== 'other').length;
    if (notation === tokens.length)
        return tokens;
    if (!tokens.some(isUnambiguousNotation) || hasPhrase(line, tokens))
        return null;
    const share = notation / tokens.length;
    return share >= 0.6 || (share >= 0.5 && WIDE_GAP.test(line)) ? tokens : null;
};
/**
 * "G   Hello darkness": chords at the start of a lyric line, set off from the
 * words by two or more spaces. Needs unambiguous notation ("A  long time"
 * stays lyrics).
 */
export const readLeadingChords = (line) => {
    const tokens = tokenizeLine(line);
    let run = 0;
    while (run < tokens.length && tokens[run].kind !== 'other')
        run++;
    if (run === 0 || run === tokens.length)
        return null;
    if (!tokens.slice(0, run).some(isUnambiguousNotation))
        return null;
    const last = tokens[run - 1];
    const gap = line.slice(last.index + last.text.length, tokens[run].index);
    if (gap.length < 2 || gap.trim() !== '')
        return null;
    return { tokens: tokens.slice(0, run), lyricsIndex: tokens[run].index };
};
/**
 * What a chord-line token puts in its slot. On a line already known to be a
 * chord line, a lowercase root ("am", "f#m") is read as the chord it means;
 * anything else unrecognized is kept verbatim as custom text.
 */
export const toChartToken = (token) => {
    if (token.chord)
        return { kind: 'chord', chord: token.chord };
    if (/^[a-g]/.test(token.text)) {
        const chord = readChord(token.text[0].toUpperCase() + token.text.slice(1));
        if (chord)
            return { kind: 'chord', chord };
    }
    return { kind: 'custom', text: token.text };
};
